/**
 * PropertyTypeIcon Component
 * 
 * Displays an icon representing the property type.
 * Used throughout the property level system.
 */

import React from 'react';
import {
  FontColorsOutlined,
  NumberOutlined,
  CheckSquareOutlined,
  CalendarOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  LinkOutlined,
  KeyOutlined,
  UnorderedListOutlined,
  FieldStringOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import { Tooltip, Tag } from 'antd';
import { PropertyType } from '../../types/propertyLevel';

interface PropertyTypeIconProps {
  type: PropertyType | string;
  showLabel?: boolean;
  size?: 'small' | 'default' | 'large';
  showTooltip?: boolean;
}

const TYPE_CONFIG: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
  string: {
    icon: <FontColorsOutlined />,
    color: 'blue',
    label: 'String'
  },
  number: {
    icon: <NumberOutlined />,
    color: 'green',
    label: 'Number'
  },
  integer: {
    icon: <NumberOutlined />,
    color: 'green',
    label: 'Integer'
  },
  float: {
    icon: <NumberOutlined />,
    color: 'green',
    label: 'Float'
  },
  boolean: {
    icon: <CheckSquareOutlined />,
    color: 'orange',
    label: 'Boolean'
  },
  date: {
    icon: <CalendarOutlined />,
    color: 'purple',
    label: 'Date'
  },
  date_range: {
    icon: <CalendarOutlined />,
    color: 'purple',
    label: 'Date Range'
  },
  currency: {
    icon: <DollarOutlined />,
    color: 'cyan',
    label: 'Currency'
  },
  duration: {
    icon: <ClockCircleOutlined />,
    color: 'geekblue',
    label: 'Duration'
  },
  url: {
    icon: <LinkOutlined />,
    color: 'gold',
    label: 'URL'
  },
  uuid: {
    icon: <KeyOutlined />,
    color: 'magenta',
    label: 'UUID'
  },
  'uuid[]': {
    icon: <KeyOutlined />,
    color: 'red',
    label: 'UUID Array'
  },
  list: {
    icon: <UnorderedListOutlined />,
    color: 'lime',
    label: 'List'
  }
};

const DEFAULT_CONFIG = {
  icon: <QuestionCircleOutlined />,
  color: 'default',
  label: 'Unknown'
};

export const PropertyTypeIcon: React.FC<PropertyTypeIconProps> = ({
  type,
  showLabel = false,
  size = 'default',
  showTooltip = true
}) => {
  const config = TYPE_CONFIG[type] || DEFAULT_CONFIG;
  
  const fontSize = size === 'small' ? 12 : size === 'large' ? 20 : 16;
  
  const iconElement = (
    <span style={{ fontSize, color: `var(--ant-color-${config.color})` }}>
      {config.icon}
    </span>
  );
  
  if (showLabel) {
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.label}
      </Tag>
    );
  }
  
  if (showTooltip) {
    return (
      <Tooltip title={config.label}>
        {iconElement}
      </Tooltip>
    );
  }
  
  return iconElement;
};

/**
 * Get the color for a property type (for use with Ant Design Tag component)
 */
export const getPropertyTypeColor = (type: PropertyType | string): string => {
  return TYPE_CONFIG[type]?.color || DEFAULT_CONFIG.color;
};

/**
 * Get the label for a property type
 */
export const getPropertyTypeLabel = (type: PropertyType | string): string => {
  return TYPE_CONFIG[type]?.label || type;
};

export default PropertyTypeIcon;
